import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import client from "@/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, RefreshCw, Languages, SlidersHorizontal, ScrollText, Search, Check, X, Ban, Plus, ClipboardList, AlertTriangle, Pencil, ShieldPlus, Trash2, KeyRound, FileCheck2, Rocket, Crown, CheckSquare, Square, Download, RotateCcw } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";

export default function Admin() {
  const { t } = useApp();
  return (
    <div className="min-h-screen bg-[#0d0d0d] bg-grain">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-[#FFFFFF] mb-8">{t("nav_admin")}</h1>
        <Tabs defaultValue="users">
          <TabsList className="bg-[#141414] border border-[#D21F2A]/20 flex-wrap h-auto">
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1" />{t("admin_users")}</TabsTrigger>
            <TabsTrigger value="reattempts" data-testid="tab-reattempts"><RefreshCw className="w-4 h-4 mr-1" />{t("admin_reattempts")}</TabsTrigger>
            <TabsTrigger value="review" data-testid="tab-review"><ClipboardList className="w-4 h-4 mr-1" />{t("admin_review")}</TabsTrigger>
            <TabsTrigger value="corrections" data-testid="tab-corrections"><FileCheck2 className="w-4 h-4 mr-1" />{t("admin_corrections")}</TabsTrigger>
            <TabsTrigger value="release" data-testid="tab-release"><Rocket className="w-4 h-4 mr-1" />{t("admin_release")}</TabsTrigger>
            <TabsTrigger value="completeness" data-testid="tab-completeness"><Languages className="w-4 h-4 mr-1" />{t("admin_completeness")}</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config"><SlidersHorizontal className="w-4 h-4 mr-1" />{t("admin_config")}</TabsTrigger>
            <TabsTrigger value="manage" data-testid="tab-manage"><ShieldPlus className="w-4 h-4 mr-1" />{t("admin_manage")}</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs"><ScrollText className="w-4 h-4 mr-1" />{t("admin_logs")}</TabsTrigger>
          </TabsList>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="reattempts"><ReattemptsTab /></TabsContent>
          <TabsContent value="review"><ReviewTab /></TabsContent>
          <TabsContent value="corrections"><CorrectionsTab /></TabsContent>
          <TabsContent value="release"><ReleaseTab /></TabsContent>
          <TabsContent value="completeness"><CompletenessTab /></TabsContent>
          <TabsContent value="config"><ConfigTab /></TabsContent>
          <TabsContent value="manage"><AdminsTab /></TabsContent>
          <TabsContent value="logs"><LogsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function UsersTab() {
  const { t } = useApp();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [delFor, setDelFor] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    if (q) params.set("q", q);
    const { data } = await client.get(`/admin/users?${params}`);
    setUsers(data.users);
  }, [q, filter]);

  useEffect(() => { load(); }, [load]);

  const setAccess = async (uid, action) => {
    try { await client.post(`/admin/users/${uid}/access`, { action }); toast.success("OK"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const toggleCancel = async (u) => {
    const cancelled = u.course_status !== "abgebrochen";
    try { await client.post(`/admin/users/${u.user_id}/cancel`, { cancelled }); toast.success("OK"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const downloadPdf = async (u) => {
    try {
      const res = await client.get(`/admin/users/${u.user_id}/result/pdf`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = `Bartender_Master_Level_${u.name}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(e.response?.data?.detail || t("no_result")); }
  };
  const delUser = async () => {
    if (!delFor) return;
    try { await client.delete(`/admin/users/${delFor.user_id}`); toast.success(t("user_deleted_ok")); setDelFor(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const grantFinal = async (uid) => {
    try {
      const grantId = `grant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await client.post(`/admin/users/${uid}/grant-final-attempt`, { grant_id: grantId, reason: "Admin-Freigabe" });
      toast.success(t("grant_attempt")); load(); if (detail) openDetail(uid);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const openDetail = async (uid) => {
    const { data } = await client.get(`/admin/users/${uid}`); setDetail(data);
  };

  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#141414] border border-[#D21F2A]/20 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[#9AA0A6]" />
          <input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
            className="bg-transparent text-[#FFFFFF] outline-none flex-1 text-sm" />
        </div>
        <select data-testid="user-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm">
          <option value="">{t("filter_all")}</option>
          <option value="aktiv">{t("cs_aktiv")}</option>
          <option value="bestanden">{t("cs_bestanden")}</option>
          <option value="nicht_bestanden">{t("cs_nicht_bestanden")}</option>
          <option value="abgebrochen">{t("cs_abgebrochen")}</option>
          <option value="pending">pending</option>
          <option value="approved">approved</option>
          <option value="rejected">rejected</option>
          <option value="blocked">blocked</option>
        </select>
      </div>

      <div className="space-y-2" data-testid="admin-user-list">
        {users.length === 0 && <p className="text-[#9AA0A6] text-sm">{t("empty")}</p>}
        {users.map((u) => (
          <div key={u.user_id} className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl bg-[#141414] border border-[#D21F2A]/10" data-testid={`admin-user-${u.user_id}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[#FFFFFF] text-sm">{u.name} {u.role === "admin" && <span className="text-[#D21F2A]">★</span>}</div>
              <div className="text-xs text-[#9AA0A6]">{u.email} · {u.providers.join(",") || "—"} · Final {u.final_started}/{u.final_allowed}</div>
            </div>
            <div className="text-xs text-[#C9CDD3] min-w-[150px]" data-testid={`user-summary-${u.user_id}`}>
              <div><CourseStatusBadge status={u.course_status} /></div>
              <div className="text-[10px] text-[#9AA0A6] mt-1">{t("progress_label")}: {u.progress_completed}/{u.progress_total}
                {u.has_result && <> · {u.final_percent}%</>}
                {u.pending_reattempt && <> · <span className="text-[#93C5FD]">{t("pending_reattempt_label")}</span></>}
              </div>
            </div>
            <StatusBadge status={u.access_status} label={u.access_status} />
            <div className="flex flex-wrap gap-1">
              <IconBtn testid={`approve-${u.user_id}`} title={t("approve")} onClick={() => setAccess(u.user_id, "approve")} icon={Check} tone="green" />
              <IconBtn testid={`reject-${u.user_id}`} title={t("reject")} onClick={() => setAccess(u.user_id, "reject")} icon={X} tone="red" />
              <IconBtn testid={`block-${u.user_id}`} title={t("block")} onClick={() => setAccess(u.user_id, "block")} icon={Ban} tone="violet" />
              <IconBtn testid={`grant-${u.user_id}`} title={t("grant_attempt")} onClick={() => grantFinal(u.user_id)} icon={Plus} tone="gold" />
              {u.has_result && <IconBtn testid={`pdf-${u.user_id}`} title={t("download_result")} onClick={() => downloadPdf(u)} icon={Download} tone="gold" />}
              <IconBtn testid={`cancel-${u.user_id}`} title={u.course_status === "abgebrochen" ? t("reactivate_course") : t("cancel_course")} onClick={() => toggleCancel(u)} icon={RotateCcw} tone="violet" />
              <IconBtn testid={`delete-user-${u.user_id}`} title={t("participant_delete")} onClick={() => setDelFor(u)} icon={Trash2} tone="red" />
              <button data-testid={`detail-${u.user_id}`} onClick={() => openDetail(u.user_id)}
                className="focusable text-xs px-2 py-1.5 rounded-lg border border-[#D21F2A]/20 text-[#C9CDD3]">{t("details")}</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#141414] border border-[#D21F2A]/30 text-[#FFFFFF] max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && <>
            <DialogHeader><DialogTitle className="font-serif">{detail.user.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-[#C9CDD3]">{detail.user.email}</p>
            <div className="flex gap-2 my-3"><StatusBadge status={detail.access.status} label={detail.access.status} />
              <span className="text-xs text-[#9AA0A6]">Final: {detail.entitlement.attempts_started}/{detail.entitlement.attempts_allowed}</span></div>
            <button data-testid="detail-grant-final" onClick={() => grantFinal(detail.user.user_id)}
              className="btn-gold focusable text-sm px-4 py-2 rounded-lg mb-4">{t("grant_attempt")}</button>
            <h4 className="font-serif text-lg mt-2 mb-2">Kapiteltests</h4>
            {detail.chapter_attempts.length === 0 ? <p className="text-xs text-[#9AA0A6]">{t("empty")}</p> :
              <div className="space-y-1">{detail.chapter_attempts.slice(0, 20).map((a, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-white/5 py-1">
                  <span className="text-[#C9CDD3]">{a.chapter_title}</span><span className="text-[#D21F2A]">{a.percent}%</span>
                </div>))}</div>}
            <h4 className="font-serif text-lg mt-4 mb-2">Finalversuche</h4>
            {detail.final_attempts.length === 0 ? <p className="text-xs text-[#9AA0A6]">{t("empty")}</p> :
              <div className="space-y-1">{detail.final_attempts.map((a, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-white/5 py-1">
                  <StatusBadge status={a.status === "open" ? "in_progress" : (a.passed ? "passed" : "failed")} label={a.status === "open" ? "offen" : (a.passed ? t("passed") : t("failed"))} />
                  <span className="text-[#D21F2A]">{a.status === "submitted" ? `${a.percent}%` : "—"}</span>
                </div>))}</div>}
          </>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delFor} onOpenChange={(o) => !o && setDelFor(null)}>
        <DialogContent className="bg-[#141414] border border-[#7F1D1D] text-[#FFFFFF] max-w-md" data-testid="delete-user-dialog">
          {delFor && <>
            <DialogHeader><DialogTitle className="font-serif">{t("participant_delete")}</DialogTitle></DialogHeader>
            <p className="text-sm text-[#C9CDD3] my-2">{delFor.name} · {delFor.email}</p>
            <p className="text-sm text-[#FCA5A5] mb-4">{t("confirm_delete_user")}</p>
            <div className="flex gap-2">
              <button data-testid="delete-user-confirm" onClick={delUser} className="focusable px-5 py-2 rounded-lg bg-[#7F1D1D] text-white text-sm">{t("participant_delete")}</button>
              <button onClick={() => setDelFor(null)} className="focusable px-5 py-2 rounded-lg border border-[#D21F2A]/20 text-[#C9CDD3] text-sm">{t("cancel")}</button>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CourseStatusBadge({ status }) {
  const { t } = useApp();
  const map = {
    aktiv: { label: t("cs_aktiv"), cls: "border-[#1E4B70] text-[#93C5FD]" },
    bestanden: { label: t("cs_bestanden"), cls: "border-[#276239] text-[#6EE7B7]" },
    nicht_bestanden: { label: t("cs_nicht_bestanden"), cls: "border-[#7F1D1D] text-[#FCA5A5]" },
    abgebrochen: { label: t("cs_abgebrochen"), cls: "border-[#4C4656] text-[#D8B4FE]" },
  };
  const s = map[status] || map.aktiv;
  return <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
}

function ReattemptsTab() {
  const { t } = useApp();
  const [reqs, setReqs] = useState([]);
  const load = useCallback(async () => { const { data } = await client.get("/admin/reattempt-requests"); setReqs(data.requests); }, []);
  useEffect(() => { load(); }, [load]);
  const grant = async (uid) => {
    try {
      const grantId = `grant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await client.post(`/admin/users/${uid}/grant-final-attempt`, { grant_id: grantId, reason: "Wiederholungsantrag genehmigt" });
      toast.success("OK"); load();
    } catch (e) { toast.error(t("error")); }
  };
  const deny = async (rid) => { try { await client.post(`/admin/reattempt-requests/${rid}/deny`); load(); } catch (e) { toast.error(t("error")); } };
  return (
    <div className="mt-6 space-y-2" data-testid="reattempt-list">
      {reqs.length === 0 && <p className="text-[#9AA0A6] text-sm">{t("empty")}</p>}
      {reqs.map((r) => (
        <div key={r.request_id} className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl bg-[#141414] border border-[#D21F2A]/10">
          <div className="flex-1 min-w-[180px]">
            <div className="text-[#FFFFFF] text-sm">{r.user?.name} · {r.user?.email}</div>
            {r.message && <div className="text-xs text-[#9AA0A6]">{r.message}</div>}
          </div>
          <StatusBadge status={r.status === "pending" ? "reattempt" : (r.status === "granted" ? "passed" : "failed")} label={r.status} />
          {r.status === "pending" && <div className="flex gap-2">
            <button data-testid={`grant-req-${r.request_id}`} onClick={() => grant(r.user_id)} className="btn-gold focusable text-xs px-3 py-1.5 rounded-lg">{t("grant_attempt")}</button>
            <button data-testid={`deny-req-${r.request_id}`} onClick={() => deny(r.request_id)} className="focusable text-xs px-3 py-1.5 rounded-lg border border-[#D21F2A]/20 text-[#C9CDD3]">{t("deny")}</button>
          </div>}
        </div>
      ))}
    </div>
  );
}

function ReviewTab() {
  const { t } = useApp();
  const [chapters, setChapters] = useState([]);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [cmpLang, setCmpLang] = useState("");
  const [cmp, setCmp] = useState({});

  useEffect(() => { client.get("/admin/completeness").then(({ data }) => setChapters(data.chapters)); }, []);
  const load = useCallback(async (id, compare) => {
    if (!id) return;
    const { data } = await client.get(`/admin/chapters/${id}/questions?lang=de`);
    setData(data);
    if (compare) {
      const { data: cd } = await client.get(`/admin/chapters/${id}/questions?lang=${compare}`);
      const m = {}; cd.questions.forEach((q) => { m[q.question_id] = q.translation; });
      setCmp(m);
    } else { setCmp({}); }
  }, []);
  useEffect(() => { if (cid) load(cid, cmpLang); }, [cid, cmpLang, load]);

  const setStatus = async (qid, status) => {
    try { await client.put(`/admin/questions/${qid}/status`, { status, apply_translation_lang: "de" }); toast.success("OK"); load(cid, cmpLang); }
    catch (e) { toast.error(t("error")); }
  };

  const badge = (q) => {
    const s = q.status === "published" ? "passed" : q.status === "review" ? "reattempt" : "not_started";
    const lbl = q.status === "published" ? t("ed_published") : q.status === "review" ? t("ed_review") : t("ed_draft");
    return <StatusBadge status={s} label={lbl} />;
  };

  return (
    <div className="mt-6">
      <p className="text-xs text-[#9AA0A6] mb-4">
        KI-Herkunft und Freigabe sind getrennt: ein KI-Entwurf kann geprüft und freigegeben sein; die KI-Markierung allein ist keine Freigabe.
      </p>
      <select data-testid="review-chapter-select" value={cid} onChange={(e) => setCid(e.target.value)}
        className="px-3 py-2 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm mb-5 min-w-[280px]">
        <option value="">{t("select_chapter")}</option>
        {chapters.map((c) => <option key={c.chapter_id} value={c.chapter_id}>{c.order}. {c.title}</option>)}
      </select>
      <select data-testid="review-compare-lang" value={cmpLang} onChange={(e) => setCmpLang(e.target.value)}
        className="px-3 py-2 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm mb-5 ml-3">
        <option value="">Nur Deutsch</option>
        <option value="en">Vergleich: English</option>
        <option value="es">Vergleich: Español</option>
        <option value="it">Vergleich: Italiano</option>
        <option value="ru">Vergleich: Русский</option>
        <option value="hi">Vergleich: हिन्दी</option>
        <option value="pt-PT">Vergleich: Português</option>
        <option value="el">Vergleich: Ελληνικά</option>
      </select>

      {data && (
        <div className="space-y-4" data-testid="review-questions">
          {data.questions.map((q) => {
            const tr = q.translation || {};
            const ct = cmpLang ? (cmp[q.question_id] || null) : null;
            return (
              <div key={q.question_id} className="card-dark rounded-2xl p-5" data-testid={`review-q-${q.question_id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {badge(q)}
                  <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#D21F2A]/30 text-[#C9CDD3]">
                    {tr.is_ai_draft === false ? t("human_edited") : t("ki_origin")}
                  </span>
                  {q.needs_review_flag && <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#E0A106] text-[#FDE047] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{t("needs_review_label")}</span>}
                  <span className="text-[10px] text-[#9AA0A6] ml-auto">{t("pdf_section")}: S.{q.source_page} · {q.section_heading}</span>
                </div>
                <p className="text-[#FFFFFF] font-medium mb-3">{tr.prompt}</p>
                <div className="grid sm:grid-cols-2 gap-2 mb-3">
                  {["a", "b", "c", "d"].map((oid) => {
                    const isC = oid === q.correct_option_id;
                    return (
                      <div key={oid} className={`text-sm px-3 py-2 rounded-lg border flex items-center gap-2 ${isC ? "border-[#276239] bg-[#142E1F] text-[#6EE7B7]" : "border-[#D21F2A]/10 text-[#C9CDD3]"}`}>
                        {isC && <Check className="w-3.5 h-3.5" />}<span className="uppercase text-[10px] opacity-60">{oid}</span>
                        <span>{tr.options?.[oid]}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-[#C9CDD3] mb-3"><span className="text-[#D21F2A]">{t("explanation")}: </span>{tr.explanation}</p>
                {cmpLang && (
                  <div className="mt-1 mb-3 p-3 rounded-lg bg-[#0d0d0d] border border-[#D21F2A]/10" data-testid={`compare-${q.question_id}`}>
                    <div className="text-[10px] uppercase tracking-wider text-[#D21F2A] mb-1">{cmpLang} {ct ? (ct.status === "published" ? "· freigegeben" : "· Entwurf") : "· fehlt"}</div>
                    {ct ? <>
                      <p className="text-sm text-[#FFFFFF]">{ct.prompt}</p>
                      <div className="grid sm:grid-cols-2 gap-1 mt-1">
                        {["a","b","c","d"].map((oid)=>(<div key={oid} className={`text-xs px-2 py-1 rounded ${oid===q.correct_option_id?"text-[#6EE7B7]":"text-[#C9CDD3]"}`}><span className="uppercase opacity-60">{oid}</span> {ct.options?.[oid]}</div>))}
                      </div>
                      <p className="text-xs text-[#9AA0A6] mt-1">{ct.explanation}</p>
                    </> : <p className="text-xs text-[#9AA0A6]">Übersetzung fehlt – bitte im Reiter „Vollständigkeit" erzeugen.</p>}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button data-testid={`edit-${q.question_id}`} onClick={() => setEdit({ ...q, tr })} className="focusable text-xs px-3 py-1.5 rounded-lg border border-[#D21F2A]/20 text-[#FFFFFF] flex items-center gap-1"><Pencil className="w-3.5 h-3.5" />{t("edit")}</button>
                  <button data-testid={`publish-${q.question_id}`} onClick={() => setStatus(q.question_id, "published")} className="btn-gold focusable text-xs px-3 py-1.5 rounded-lg">{t("publish")}</button>
                  <button data-testid={`hold-${q.question_id}`} onClick={() => setStatus(q.question_id, "review")} className="focusable text-xs px-3 py-1.5 rounded-lg border border-[#E0A106]/40 text-[#FDE047]">{t("hold")}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <QuestionEditDialog edit={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(cid); }} />
    </div>
  );
}

function QuestionEditDialog({ edit, onClose, onSaved }) {
  const { t } = useApp();
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (edit) setForm({
      prompt: edit.tr.prompt || "", options: { ...(edit.tr.options || {}) },
      explanation: edit.tr.explanation || "", correct_option_id: edit.correct_option_id,
      section_ref: edit.section_ref || "", needs_review_flag: !!edit.needs_review_flag, source_page: edit.source_page || "",
    });
  }, [edit]);
  if (!edit || !form) return null;
  const save = async () => {
    try { await client.put(`/admin/questions/${edit.question_id}`, { ...form, lang: "de" }); toast.success(t("save")); onSaved(); }
    catch (e) { toast.error(t("error")); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141414] border border-[#D21F2A]/30 text-[#FFFFFF] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif">{edit.question_id}</DialogTitle></DialogHeader>
        <label className="text-xs text-[#C9CDD3]">Frage</label>
        <textarea data-testid="edit-prompt" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          className="focusable w-full mb-3 px-3 py-2 rounded-lg bg-[#0d0d0d] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm" rows={2} />
        {["a", "b", "c", "d"].map((oid) => (
          <div key={oid} className="flex items-center gap-2 mb-2">
            <button data-testid={`set-correct-${oid}`} onClick={() => setForm({ ...form, correct_option_id: oid })}
              className={`w-6 h-6 rounded-full border-2 shrink-0 flex items-center justify-center ${form.correct_option_id === oid ? "border-[#6EE7B7] bg-[#142E1F]" : "border-[#9AA0A6]"}`}>
              {form.correct_option_id === oid && <Check className="w-3 h-3 text-[#6EE7B7]" />}
            </button>
            <span className="uppercase text-[10px] w-4 text-[#9AA0A6]">{oid}</span>
            <input data-testid={`edit-opt-${oid}`} value={form.options[oid] || ""} onChange={(e) => setForm({ ...form, options: { ...form.options, [oid]: e.target.value } })}
              className="focusable flex-1 px-3 py-2 rounded-lg bg-[#0d0d0d] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm" />
          </div>
        ))}
        <label className="text-xs text-[#C9CDD3] mt-2">{t("explanation")}</label>
        <textarea data-testid="edit-explanation" value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })}
          className="focusable w-full mb-3 px-3 py-2 rounded-lg bg-[#0d0d0d] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm" rows={2} />
        <div className="flex items-center gap-3 mb-4">
          <label className="flex items-center gap-2 text-sm text-[#C9CDD3]">
            <input data-testid="edit-needs-review" type="checkbox" checked={form.needs_review_flag} onChange={(e) => setForm({ ...form, needs_review_flag: e.target.checked })} />
            {t("needs_review_label")}
          </label>
          <label className="text-sm text-[#C9CDD3]">PDF-Seite
            <input value={form.source_page} onChange={(e) => setForm({ ...form, source_page: e.target.value })}
              className="focusable ml-2 w-16 px-2 py-1 rounded bg-[#0d0d0d] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm" />
          </label>
        </div>
        <div className="flex gap-2">
          <button data-testid="edit-save" onClick={save} className="btn-gold focusable px-5 py-2 rounded-lg text-sm">{t("save")}</button>
          <button onClick={onClose} className="focusable px-5 py-2 rounded-lg border border-[#D21F2A]/20 text-[#C9CDD3] text-sm">{t("cancel")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompletenessTab() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => { const { data } = await client.get("/admin/completeness"); setData(data); }, []);
  useEffect(() => { load(); }, [load]);
  const translate = async (chapterId, lang) => {
    setBusy(`${chapterId}-${lang}`);
    try {
      await client.post("/admin/translate", { chapter_id: chapterId, lang });
      await client.post("/admin/translate-questions", { chapter_id: chapterId, lang });
      toast.success(t("translate_ai")); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    finally { setBusy(""); }
  };
  if (!data) return <p className="mt-6 text-[#9AA0A6]">{t("loading")}</p>;
  return (
    <div className="mt-6" data-testid="completeness-table">
      {/* Kapitel-Prüfübersicht */}
      <div className="overflow-x-auto mb-8">
        <table className="w-full text-sm border-collapse">
          <thead><tr className="text-left text-[#D21F2A]">
            <th className="p-2">{t("chapter")}</th><th className="p-2">PDF</th><th className="p-2">{t("sections_lbl")}</th>
            <th className="p-2">Test</th><th className="p-2">{t("mandatory")}</th>
            <th className="p-2">Fragen ({t("usable_q")}/{t("of_target")})</th><th className="p-2">DE</th>
          </tr></thead>
          <tbody>
            {data.chapters.map((c) => (
              <tr key={c.chapter_id} className="border-t border-white/5" data-testid={`comp-row-${c.chapter_id}`}>
                <td className="p-2 text-[#FFFFFF]">{c.order}. {c.title}</td>
                <td className="p-2 text-[#9AA0A6]">S.{c.pdf_pages}</td>
                <td className="p-2 text-[#C9CDD3] text-center">{c.sections_count}</td>
                <td className="p-2 text-center">{c.has_test ? <Check className="w-4 h-4 text-[#6EE7B7] mx-auto" /> : <X className="w-4 h-4 text-[#9AA0A6] mx-auto" />}</td>
                <td className="p-2 text-center">{c.mandatory ? <span className="text-[10px] text-[#6EE7B7]">Pflicht</span> : <span className="text-[10px] text-[#FDE047]">optional</span>}</td>
                <td className="p-2 text-center">
                  <span className={c.questions_usable >= c.question_target ? "text-[#6EE7B7]" : "text-[#FCA5A5]"}>{c.questions_usable}</span>
                  <span className="text-[#9AA0A6]">/{c.question_target}</span>
                  {c.questions_needs_review > 0 && <span className="ml-1 text-[10px] text-[#FDE047]" title={t("needs_review_label")}>⚑{c.questions_needs_review}</span>}
                </td>
                <td className="p-2 text-center"><span className={`text-[10px] ${c.de_content_status === "published" ? "text-[#6EE7B7]" : "text-[#FDE047]"}`}>{c.de_content_status}</span>{c.de_is_ai && <span className="text-[10px] text-[#9AA0A6]"> · KI</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-[#9AA0A6] mt-3" data-testid="optional-chapter-note">
          Hinweis: Kapitel 04 „Stimmen von der Bar" ist das einzige <b>optionale</b> Kapitel (kein Pflichtkapitel) — deshalb 30 Pflichtkapitel von 31. {t("optional_chapter_note")}
        </p>
      </div>

      {/* Sprach-Matrix */}
      <h3 className="font-serif text-lg text-[#FFFFFF] mb-2">Sprachen (Inhalt / freigegebene Fragen)</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead><tr className="text-left text-[#D21F2A]">
            <th className="p-2">{t("chapter")}</th>
            {data.languages.map((l) => <th key={l.code} className="p-2 text-center">{l.code}</th>)}
          </tr></thead>
          <tbody>
            {data.chapters.map((c) => (
              <tr key={c.chapter_id} className="border-t border-white/5">
                <td className="p-2 text-[#FFFFFF]">{c.order}. {c.title}</td>
                {data.languages.map((l) => {
                  const st = c.languages[l.code];
                  const ok = st.content_status === "published";
                  return (
                    <td key={l.code} className="p-2 text-center">
                      {l.code === "de" ? <Check className="w-4 h-4 text-[#6EE7B7] mx-auto" /> :
                        st.content_status === "missing" ? (
                          <button disabled={busy === `${c.chapter_id}-${l.code}`} data-testid={`translate-${c.chapter_id}-${l.code}`}
                            onClick={() => translate(c.chapter_id, l.code)}
                            className="focusable text-[10px] px-2 py-1 rounded border border-[#D21F2A]/30 text-[#C9CDD3] hover:text-[#D21F2A]">
                            {busy === `${c.chapter_id}-${l.code}` ? "…" : "KI"}
                          </button>
                        ) : (
                          <span className={`text-[10px] ${ok ? "text-[#6EE7B7]" : "text-[#FDE047]"}`} title={`Fragen: ${st.questions_published} freigegeben`}>{st.content_status}<br />{st.questions_published}Q</span>
                        )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#9AA0A6] mt-4">DE = geprüfte Basis (veröffentlicht). „KI" erzeugt Übersetzungsentwürfe für Inhalt UND Fragen (Status: draft), die redaktionell geprüft und veröffentlicht werden müssen. Unvollständige Sprachen werden Teilnehmern nicht als fertige Kursversion angezeigt (DE-Fallback).</p>
    </div>
  );
}

function ConfigTab() {
  const { t } = useApp();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { client.get("/admin/config").then(({ data }) => setCfg(data)); }, []);
  const save = async () => {
    try { const { data } = await client.put("/admin/config", cfg); setCfg(data); toast.success(t("save")); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  if (!cfg) return <p className="mt-6 text-[#9AA0A6]">{t("loading")}</p>;
  return (
    <div className="mt-6 max-w-md space-y-4" data-testid="config-form">
      <Field label={t("chapter_hint")} value={cfg.chapter_pass_hint} onChange={(v) => setCfg({ ...cfg, chapter_pass_hint: v })} testid="cfg-chapter-hint" />
      <Field label={t("final_qcount")} value={cfg.final_question_count} onChange={(v) => setCfg({ ...cfg, final_question_count: v })} testid="cfg-final-qcount" />
      <Field label={t("final_pass_mark")} value={cfg.final_pass_mark} onChange={(v) => setCfg({ ...cfg, final_pass_mark: v })} testid="cfg-final-passmark" />
      <button data-testid="config-save" onClick={save} className="btn-gold focusable px-6 py-2.5 rounded-lg">{t("save")}</button>
    </div>
  );
}

function ReleaseTab() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    const { data } = await client.get("/admin/language-status");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async (lang) => {
    if (!window.confirm(t("rel_confirm"))) return;
    setBusy(lang);
    try { await client.post(`/admin/languages/${lang}/publish`); toast.success(`${lang} ✓`); load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? `${d.message}: ${(d.issues || []).slice(0, 3).join("; ")}` : (d || t("error")));
    } finally { setBusy(""); }
  };
  const unpublish = async (lang) => {
    setBusy(lang);
    try { await client.post(`/admin/languages/${lang}/unpublish`); toast.success("OK"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); } finally { setBusy(""); }
  };

  if (!data) return <p className="mt-6 text-[#9AA0A6]">{t("loading")}</p>;
  return (
    <div className="mt-6" data-testid="release-tab">
      <div className="flex items-start gap-2 p-3 mb-5 rounded-xl bg-[#1a1a1a] border border-[#D21F2A]/20">
        <AlertTriangle className="w-4 h-4 text-[#FDE047] shrink-0 mt-0.5" />
        <p className="text-xs text-[#C9CDD3]">{t("rel_hint")}</p>
      </div>
      {data.open_decisions.length > 0 && (
        <div className="mb-5 text-xs" data-testid="release-open-decisions">
          <span className="text-[#FCA5A5] font-medium">{t("rel_open_decisions")}: </span>
          {data.open_decisions.map((o) => <span key={o.question_id} className="text-[#C9CDD3]">{o.question_id} ({o.affects}) </span>)}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="release-table">
          <thead><tr className="text-left text-[#9AA0A6] text-xs uppercase tracking-wider border-b border-[#D21F2A]/10">
            <th className="py-2 pr-4">Sprache</th><th className="pr-4">{t("rel_chapters")}</th><th className="pr-4">{t("rel_questions")}</th>
            <th className="pr-4">{t("rel_corrections")}</th><th className="pr-4">{t("rel_qa")}</th><th className="pr-4">{t("rel_pubstatus")}</th><th></th>
          </tr></thead>
          <tbody>
            {data.languages.map((l) => (
              <tr key={l.lang} className="border-b border-white/5" data-testid={`release-row-${l.lang}`}>
                <td className="py-3 pr-4 font-medium text-[#FFFFFF] uppercase">{l.lang}</td>
                <td className="pr-4 text-[#C9CDD3]">{l.chapters_translated}/{l.chapters_total}</td>
                <td className="pr-4 text-[#C9CDD3]">{l.usable_questions}</td>
                <td className="pr-4 text-[#C9CDD3]">q{l.corrections_applied.questions} · c{l.corrections_applied.content}</td>
                <td className="pr-4 text-[#93C5FD] text-xs">{l.qa_status}{!l.human_reviewed && !l.is_base ? " · keine menschl. Abnahme" : ""}</td>
                <td className="pr-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${l.publish_status === "veröffentlicht" ? "text-[#6EE7B7] border-[#6EE7B7]/40" : l.is_base ? "text-[#C9CDD3] border-white/10" : "text-[#FDE047] border-[#FDE047]/30"}`}>{l.publish_status}</span>
                </td>
                <td className="text-right">
                  {!l.is_base && l.lang !== "en" && (l.publish_status === "veröffentlicht"
                    ? <button data-testid={`unpublish-${l.lang}`} disabled={busy === l.lang} onClick={() => unpublish(l.lang)} className="focusable text-xs px-3 py-1.5 rounded-lg border border-[#FCA5A5]/40 text-[#FCA5A5]">{t("rel_unpublish")}</button>
                    : <button data-testid={`publish-${l.lang}`} disabled={busy === l.lang} onClick={() => publish(l.lang)} className="btn-gold focusable text-xs px-3 py-1.5 rounded-lg">{busy === l.lang ? "…" : t("rel_publish")}</button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CorrectionsTab() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [fLang, setFLang] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fCat, setFCat] = useState("");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (fLang) p.set("lang", fLang);
    if (fStatus) p.set("status", fStatus);
    if (fCat) p.set("category", fCat);
    const { data } = await client.get(`/admin/corrections?${p}`);
    setData(data);
  }, [fLang, fStatus, fCat]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action, reason) => {
    try {
      await client.post(`/admin/corrections/${id}/${action}`, action === "dismiss" ? { reason: reason || "" } : {});
      toast.success("OK"); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  if (!data) return <p className="mt-6 text-[#9AA0A6]">{t("loading")}</p>;
  const sevTone = { critical: "text-[#FCA5A5] border-[#FCA5A5]/40", major: "text-[#FDE047] border-[#FDE047]/40", minor: "text-[#C9CDD3] border-[#D21F2A]/20", none: "text-[#9AA0A6] border-white/10" };
  const catTone = { bedeutungsfehler: "text-[#FCA5A5]", stil: "text-[#93C5FD]", fehlalarm: "text-[#9AA0A6]" };
  const statusTone = { proposed: "reattempt", draft_applied: "in_progress", dismissed: "failed", editorially_approved: "passed" };

  return (
    <div className="mt-6" data-testid="corrections-tab">
      <p className="text-xs text-[#9AA0A6] mb-4 max-w-3xl">{t("corr_hint")}</p>
      <div className="flex flex-wrap gap-3 mb-5 text-sm">
        <select data-testid="corr-filter-lang" value={fLang} onChange={(e) => setFLang(e.target.value)} className="px-3 py-2 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-[#FFFFFF]">
          <option value="">{t("filter_all")} · {t("language")}</option><option value="es">ES</option><option value="it">IT</option><option value="ru">RU</option>
        </select>
        <select data-testid="corr-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="px-3 py-2 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-[#FFFFFF]">
          <option value="">{t("filter_all")} · {t("corr_filter_status")}</option>
          <option value="proposed">{t("corr_status_proposed")}</option>
          <option value="draft_applied">{t("corr_status_draft_applied")}</option>
          <option value="dismissed">{t("corr_status_dismissed")}</option>
          <option value="editorially_approved">{t("corr_status_editorially_approved")}</option>
        </select>
        <select data-testid="corr-filter-cat" value={fCat} onChange={(e) => setFCat(e.target.value)} className="px-3 py-2 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-[#FFFFFF]">
          <option value="">{t("filter_all")} · {t("corr_filter_cat")}</option>
          <option value="bedeutungsfehler">{t("cat_bedeutungsfehler")}</option>
          <option value="stil">{t("cat_stil")}</option>
          <option value="fehlalarm">{t("cat_fehlalarm")}</option>
        </select>
        <div className="text-xs text-[#9AA0A6] flex items-center gap-3 ml-auto" data-testid="corr-stats">
          <span>Σ {data.stats.total}</span>
          <span>{t("corr_status_draft_applied")}: {data.stats.by_status.draft_applied || 0}</span>
          <span>{t("corr_status_editorially_approved")}: {data.stats.by_status.editorially_approved || 0}</span>
        </div>
      </div>

      <div className="space-y-4" data-testid="corrections-list">
        {data.corrections.length === 0 && <p className="text-[#9AA0A6] text-sm">{t("empty")}</p>}
        {data.corrections.map((c) => {
          const changed = Object.keys(c.proposed_corrections || {}).filter((k) => ["prompt", "a", "b", "c", "d", "explanation"].includes(k));
          const applied = c.status === "draft_applied" || c.applied;
          const fieldLabel = (k) => k === "prompt" ? t("question") : k === "explanation" ? t("explanation") : `${t("of").length ? "Option" : "Option"} ${k}`;
          return (
            <div key={c.correction_id} className="card-dark rounded-2xl p-5" data-testid={`corr-${c.correction_id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#D21F2A]/30 text-[#D21F2A] uppercase">{c.lang}</span>
                <span className="text-[#FFFFFF] text-sm font-medium">{c.chapter_id} · {c.question_id}</span>
                {c.section_heading && <span className="text-[10px] text-[#9AA0A6]">{c.section_ref}: {c.section_heading}</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sevTone[c.severity] || sevTone.none}`}>{t("sev_" + c.severity)}</span>
                <span className={`text-[10px] ${catTone[c.category] || ""}`}>{t("cat_" + c.category)}</span>
                <span className="text-[10px] text-[#9AA0A6] ml-auto">{t("corr_source")}: PDF S.{c.source_page} · KI-QA</span>
                <StatusBadge status={statusTone[c.status] || "not_started"} label={t("corr_status_" + c.status)} />
              </div>

              <div className="grid md:grid-cols-3 gap-3 text-xs">
                <div className="p-3 rounded-lg bg-[#0d0d0d] border border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-[#D21F2A] mb-1">{t("corr_de_source")}</div>
                  <p className="text-[#FFFFFF] mb-1">{c.de_source?.prompt}</p>
                  {["a", "b", "c", "d"].map((k) => c.de_source?.options?.[k] && <div key={k} className={k === c.correct_option_id ? "text-[#6EE7B7]" : "text-[#C9CDD3]"}><span className="uppercase opacity-60">{k}</span> {c.de_source.options[k]}</div>)}
                  <p className="text-[#9AA0A6] mt-1">{c.de_source?.explanation}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#0d0d0d] border border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-[#C9CDD3] mb-1">{t("corr_current")} ({c.lang})</div>
                  <p className="text-[#FFFFFF] mb-1">{c.current_translation?.prompt}</p>
                  {["a", "b", "c", "d"].map((k) => c.current_translation?.options?.[k] && <div key={k} className={k === c.correct_option_id ? "text-[#6EE7B7]" : "text-[#C9CDD3]"}><span className="uppercase opacity-60">{k}</span> {c.current_translation.options[k]}</div>)}
                  <p className="text-[#9AA0A6] mt-1">{c.current_translation?.explanation}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#1a140d] border border-[#D21F2A]/20">
                  <div className="text-[10px] uppercase tracking-wider text-[#D21F2A] mb-1">{t("corr_proposed")}</div>
                  {changed.length === 0 ? <p className="text-[#9AA0A6] italic">{t("corr_no_text")}</p> :
                    changed.map((k) => <div key={k} className="mb-1"><span className="text-[10px] uppercase text-[#9AA0A6]">{fieldLabel(k)}: </span><span className="text-[#FFFFFF]">{c.proposed_corrections[k]}</span></div>)}
                </div>
              </div>

              {c.findings?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#C9CDD3] mb-1">{t("corr_reason")}</div>
                  <ul className="list-disc pl-5 text-xs text-[#C9CDD3] space-y-0.5">{c.findings.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                <button data-testid={`corr-apply-${c.correction_id}`} disabled={applied || c.status === "dismissed" || changed.length === 0}
                  onClick={() => act(c.correction_id, "apply-draft")}
                  className="btn-gold focusable text-xs px-3 py-1.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">{t("corr_apply_draft")}</button>
                <button data-testid={`corr-dismiss-${c.correction_id}`} disabled={c.status !== "proposed"}
                  onClick={() => act(c.correction_id, "dismiss")}
                  className="focusable text-xs px-3 py-1.5 rounded-lg border border-[#FCA5A5]/40 text-[#FCA5A5] disabled:opacity-40 disabled:cursor-not-allowed">{t("corr_dismiss")}</button>
                <button data-testid={`corr-approve-${c.correction_id}`} disabled={c.status === "editorially_approved"}
                  onClick={() => act(c.correction_id, "approve")}
                  className="focusable text-xs px-3 py-1.5 rounded-lg border border-[#6EE7B7]/40 text-[#6EE7B7] disabled:opacity-40 disabled:cursor-not-allowed">{t("corr_approve")}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminsTab() {
  const { t } = useApp();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  const load = useCallback(async () => {
    const { data } = await client.get("/admin/admins");
    setAdmins(data.admins);
    setSelected(new Set());
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectable = admins.filter((a) => !a.is_self);
  const allSelected = selectable.length > 0 && selectable.every((a) => selected.has(a.user_id));
  const toggleOne = (uid) => setSelected((prev) => {
    const n = new Set(prev); n.has(uid) ? n.delete(uid) : n.add(uid); return n;
  });
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(selectable.map((a) => a.user_id)));
  const selectedAdmins = admins.filter((a) => selected.has(a.user_id));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/admin/admins", form);
      toast.success(t("admin_created_ok"));
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };
  const remove = async (uid) => {
    if (!window.confirm(t("confirm_delete_admin"))) return;
    try { await client.delete(`/admin/admins/${uid}`); toast.success(t("admin_deleted_ok")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const bulkDelete = async () => {
    setConfirmBulk(false);
    try {
      const { data } = await client.post("/admin/admins/bulk-delete", { user_ids: [...selected] });
      toast.success(t("bulk_deleted", { n: data.deleted_count, s: data.skipped_count }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const resetPw = async () => {
    try {
      await client.put(`/admin/admins/${resetFor.user_id}/password`, { password: newPw });
      toast.success(t("admin_created_ok")); setResetFor(null); setNewPw("");
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  return (
    <div className="mt-6" data-testid="admins-tab">
      <p className="text-xs text-[#9AA0A6] mb-5 max-w-2xl">{t("admin_manage_hint")}</p>

      <form onSubmit={create} className="card-dark rounded-2xl p-5 mb-8 max-w-xl grid gap-3" data-testid="add-admin-form">
        <h3 className="font-serif text-lg text-[#FFFFFF] flex items-center gap-2"><ShieldPlus className="w-5 h-5 text-[#D21F2A]" />{t("admin_add")}</h3>
        <input data-testid="new-admin-name" required placeholder={t("admin_name")} value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="focusable px-4 py-2.5 rounded-lg bg-[#0d0d0d] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm" />
        <input data-testid="new-admin-email" required type="email" placeholder={t("email")} value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="focusable px-4 py-2.5 rounded-lg bg-[#0d0d0d] border border-[#D21F2A]/20 text-[#FFFFFF] text-sm" />
        <PasswordInput testid="new-admin-password" value={form.password} autoComplete="new-password"
          onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("password")} />
        <button data-testid="create-admin-btn" type="submit" disabled={busy}
          className="btn-gold focusable px-6 py-2.5 rounded-lg justify-self-start">{busy ? t("loading") : t("admin_create")}</button>
      </form>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-serif text-lg text-[#FFFFFF]">{t("admin_list_title")}</h3>
        <div className="flex items-center gap-3">
          <button data-testid="select-all-admins" onClick={toggleAll} disabled={selectable.length === 0}
            className="focusable inline-flex items-center gap-2 text-xs text-[#C9CDD3] hover:text-white disabled:opacity-40">
            {allSelected ? <CheckSquare className="w-4 h-4 text-[#D21F2A]" /> : <Square className="w-4 h-4" />} {t("select_all")}
          </button>
          <button data-testid="bulk-delete-admins" onClick={() => setConfirmBulk(true)} disabled={selected.size === 0}
            className="focusable inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-[#7F1D1D] text-[#FCA5A5] hover:bg-[#3B1414] disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash2 className="w-4 h-4" /> {t("delete_selected")} ({selected.size})
          </button>
        </div>
      </div>

      <div className="space-y-2" data-testid="admins-list">
        {admins.map((a) => (
          <div key={a.user_id} className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl bg-[#141414] border border-[#D21F2A]/10" data-testid={`admin-row-${a.user_id}`}>
            <button data-testid={`select-admin-${a.user_id}`} onClick={() => toggleOne(a.user_id)} disabled={a.is_self}
              aria-label={t("select_all")} className="focusable p-1 disabled:opacity-30">
              {selected.has(a.user_id) ? <CheckSquare className="w-5 h-5 text-[#D21F2A]" /> : <Square className="w-5 h-5 text-[#9AA0A6]" />}
            </button>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[#FFFFFF] text-sm flex items-center gap-2 flex-wrap">
                {a.name}
                {a.is_main_admin && <span data-testid={`main-admin-badge-${a.user_id}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-[#D21F2A]/40 text-[#D21F2A]"><Crown className="w-3 h-3" />{t("main_admin")}</span>}
                {a.is_self && <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#D21F2A]/30 text-[#C9CDD3]">{t("admin_self")}</span>}
              </div>
              <div className="text-xs text-[#9AA0A6]">{a.email}</div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#9AA0A6] min-w-[120px]">
              {t("admin_role")}: <span className="text-[#C9CDD3]">{t("nav_admin")}</span> · {t("admin_status")}: <span className="text-[#6EE7B7]">{a.status}</span>
            </div>
            <div className="flex gap-1">
              <IconBtn testid={`reset-pw-${a.user_id}`} title={t("admin_reset_pw")} onClick={() => setResetFor(a)} icon={KeyRound} tone="gold" />
              {!a.is_self && <IconBtn testid={`delete-admin-${a.user_id}`} title={t("admin_delete")} onClick={() => remove(a.user_id)} icon={Trash2} tone="red" />}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent className="bg-[#141414] border border-[#D21F2A]/30 text-[#FFFFFF] max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader><DialogTitle className="font-serif">{t("confirm_bulk_delete", { n: selected.size })}</DialogTitle></DialogHeader>
          <div className="max-h-52 overflow-auto my-3 space-y-1">
            {selectedAdmins.map((a) => (
              <div key={a.user_id} className="text-sm text-[#C9CDD3] flex items-center gap-2">
                {a.is_main_admin && <Crown className="w-3.5 h-3.5 text-[#D21F2A]" />}{a.name} <span className="text-[#9AA0A6]">· {a.email}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#FCA5A5] mb-4">{t("bulk_delete_warn")}</p>
          <div className="flex gap-2">
            <button data-testid="bulk-delete-confirm" onClick={bulkDelete} className="focusable px-5 py-2 rounded-lg bg-[#7F1D1D] text-white text-sm">{t("admin_delete")}</button>
            <button onClick={() => setConfirmBulk(false)} className="focusable px-5 py-2 rounded-lg border border-[#D21F2A]/20 text-[#C9CDD3] text-sm">{t("cancel")}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) { setResetFor(null); setNewPw(""); } }}>
        <DialogContent className="bg-[#141414] border border-[#D21F2A]/30 text-[#FFFFFF] max-w-md">
          {resetFor && <>
            <DialogHeader><DialogTitle className="font-serif">{t("admin_reset_pw")} · {resetFor.name}</DialogTitle></DialogHeader>
            <div className="my-4"><PasswordInput testid="reset-pw-input" value={newPw} autoComplete="new-password"
              onChange={(e) => setNewPw(e.target.value)} placeholder={t("admin_new_pw")} /></div>
            <div className="flex gap-2">
              <button data-testid="reset-pw-save" onClick={resetPw} className="btn-gold focusable px-5 py-2 rounded-lg text-sm">{t("save")}</button>
              <button onClick={() => { setResetFor(null); setNewPw(""); }} className="focusable px-5 py-2 rounded-lg border border-[#D21F2A]/20 text-[#C9CDD3] text-sm">{t("cancel")}</button>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LogsTab() {
  const { t } = useApp();
  const [logs, setLogs] = useState([]);
  useEffect(() => { client.get("/admin/logs").then(({ data }) => setLogs(data.logs)); }, []);
  return (
    <div className="mt-6 space-y-1" data-testid="logs-list">
      {logs.length === 0 && <p className="text-[#9AA0A6] text-sm">{t("empty")}</p>}
      {logs.map((l, i) => (
        <div key={i} className="flex flex-wrap gap-2 text-xs px-3 py-2 rounded-lg bg-[#141414] border border-white/5">
          <span className="text-[#9AA0A6]">{new Date(l.timestamp).toLocaleString()}</span>
          <span className="text-[#D21F2A]">{l.action}</span>
          <span className="text-[#C9CDD3]">{l.target_user || ""}</span>
          <span className="text-[#9AA0A6]">{JSON.stringify(l.details)}</span>
        </div>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{label}</label>
      <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(parseInt(e.target.value || 0))}
        className="focusable w-full px-4 py-2.5 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-[#FFFFFF]" />
    </div>
  );
}

function IconBtn({ icon: Icon, title, onClick, tone, testid }) {
  const tones = { green: "text-[#6EE7B7]", red: "text-[#FCA5A5]", violet: "text-[#D8B4FE]", gold: "text-[#D21F2A]" };
  return (
    <button data-testid={testid} title={title} onClick={onClick}
      className={`focusable p-1.5 rounded-lg border border-[#D21F2A]/15 hover:bg-white/5 transition-colors ${tones[tone]}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}
